import { useEffect, useMemo, useRef } from 'react';
import { Resolver, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { getSubmitFormConfig } from './getSubmitFormConfig.ts';
import { useFormInitialValues } from './useFormInitialValues.ts';
import { ApplicationSubmitFormValues, FormDataSubmit } from '../types.ts';

export const useApplicationSubmitForm = (
  current: number,
  formData: FormDataSubmit | undefined
) => {
  const { initialValues } = useFormInitialValues(formData);

  const currentStepRef = useRef(current);
  const formDataRef = useRef<FormDataSubmit | undefined>(formData);

  useEffect(() => {
    currentStepRef.current = current;
  }, [current]);

  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  const resolver = useMemo<Resolver<ApplicationSubmitFormValues>>(
    () => async (values, context, options) => {
      const { validationSchema } = getSubmitFormConfig(currentStepRef.current, formDataRef.current);

      if (!validationSchema) {
        return { values, errors: {} };
      }

      const stepResolver = yupResolver(validationSchema) as Resolver<ApplicationSubmitFormValues>;
      return stepResolver(values, context, options);
    },
    []
  );

  const form = useForm<ApplicationSubmitFormValues>({
    defaultValues: initialValues,
    resolver,
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  useEffect(() => {
    form.reset(initialValues);
  }, [form, initialValues]);

  return form;
};
